import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  assetUrl,
  fetchLibrary,
  type Band,
  type LibraryEntry,
  type LibrarySection,
  type SlotRole,
} from "../../infra/api.js";
import { Button } from "../../ui/Button.js";
import { LpcAvatar } from "./LpcAvatar.js";
import { SongCover } from "./SongCover.js";

type SortKey = "recent" | "oldest" | "band";

// Order in which we surface a "feature" slot — vocal first, per spec §F7.
const SLOT_PRIORITY: SlotRole[] = ["vocal", "guitar", "bass", "keys", "drums"];

function pickFeatureSlot(band: Band | null): Band["slots"][number] | null {
  if (!band) return null;
  const byRole = new Map(band.slots.map((s) => [s.slot_role, s] as const));
  for (const role of SLOT_PRIORITY) {
    const s = byRole.get(role);
    if (s) return s;
  }
  return null;
}

function compareForSort(sort: SortKey, a: LibraryEntry, b: LibraryEntry): number {
  if (sort === "band") {
    const ba = a.band?.name ?? "~";
    const bb = b.band?.name ?? "~";
    const cmp = ba.localeCompare(bb);
    if (cmp !== 0) return cmp;
    return b.song.created_at.localeCompare(a.song.created_at);
  }
  return sort === "recent"
    ? b.song.created_at.localeCompare(a.song.created_at)
    : a.song.created_at.localeCompare(b.song.created_at);
}

export function Library(): JSX.Element {
  const [entries, setEntries] = useState<LibraryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedBands, setSelectedBands] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortKey>("recent");

  const reload = useCallback((bandIds: Set<string>): AbortController => {
    const ctl = new AbortController();
    setError(null);
    void (async () => {
      try {
        const ids = Array.from(bandIds);
        const filter: { bandIds?: string[] } = {};
        if (ids.length > 0) filter.bandIds = ids;
        const { entries: list } = await fetchLibrary(filter, ctl.signal);
        if (!ctl.signal.aborted) setEntries(list);
      } catch (e) {
        if (ctl.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
        setEntries([]);
      }
    })();
    return ctl;
  }, []);

  useEffect(() => {
    const ctl = reload(selectedBands);
    return () => ctl.abort();
  }, [reload, selectedBands]);

  // Distinct bands appearing in the (unfiltered + filtered) entries — feeds the
  // multi-select. We compute from the currently-loaded entries plus
  // selectedBands so a band can still be unchecked after the filter narrows.
  const knownBands = useMemo(() => {
    const map = new Map<string, Band>();
    for (const e of entries ?? []) {
      if (e.band) map.set(e.band.id, e.band);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const visible = useMemo(() => {
    if (!entries) return [];
    const list = entries.slice();
    list.sort((a, b) => compareForSort(sort, a, b));
    return list;
  }, [entries, sort]);

  const toggleBand = (id: string): void => {
    setSelectedBands((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const loading = entries === null;
  const empty = !loading && visible.length === 0;

  return (
    <div className="flex flex-col gap-8">
      <header className="reveal grid grid-cols-12 gap-6 border-b border-border pb-8">
        <div className="col-span-12 md:col-span-8">
          <p className="catalog-num mb-3">CATÁLOGO / PRESSING LIST</p>
          <h1
            className="font-display text-[64px] leading-[0.88] tracking-tight text-paper md:text-[88px]"
            style={{ fontVariationSettings: '"opsz" 144, "wght" 600, "SOFT" 100, "WONK" 1' }}
          >
            Todas as <span className="italic text-vinyl">prensagens</span>
          </h1>
          <p className="mt-4 max-w-prose text-sm text-ink-muted">
            Tudo o que saiu da fita: faixas com áudio, agrupadas por banda. Cada cartão dá pra ouvir
            inline, seção por seção, sem sair do catálogo.
          </p>
        </div>
        <div className="col-span-12 flex flex-col items-end justify-end gap-3 md:col-span-4">
          <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
            ordenar
            <select
              className="border border-border bg-asphalt px-3 py-2 font-mono text-[11px] uppercase tracking-widest2 text-paper focus:border-paper focus:outline-none"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              aria-label="ordenar"
            >
              <option value="recent">recentes</option>
              <option value="oldest">antigas</option>
              <option value="band">por banda</option>
            </select>
          </label>
        </div>
      </header>

      {knownBands.length > 0 && (
        <div className="reveal-1 flex flex-wrap items-center gap-2" aria-label="filtrar por banda">
          <span className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
            bandas:
          </span>
          {knownBands.map((b) => {
            const active = selectedBands.has(b.id);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => toggleBand(b.id)}
                aria-pressed={active}
                className={
                  "border px-3 py-1 font-mono text-[10px] uppercase tracking-widest2 transition-colors " +
                  (active
                    ? "border-paper bg-paper text-asphalt"
                    : "border-border bg-transparent text-paper hover:border-paper")
                }
              >
                {b.name}
              </button>
            );
          })}
          {selectedBands.size > 0 && (
            <button
              type="button"
              onClick={() => setSelectedBands(new Set())}
              className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted underline-offset-2 hover:text-paper hover:underline"
            >
              limpar filtro
            </button>
          )}
        </div>
      )}

      {loading && (
        <p role="status" className="font-mono text-xs uppercase tracking-widest2 text-ink-muted">
          Carregando…
        </p>
      )}
      {error && !loading && (
        <p
          role="alert"
          className="border border-vinyl/40 px-4 py-3 font-mono text-xs uppercase tracking-widest2 text-vinyl"
        >
          erro ao carregar a galeria: {error}
        </p>
      )}
      {empty && !error && (
        <div className="reveal-1 border border-dashed border-border p-12 text-center">
          <p className="font-display text-2xl text-paper">Fita virgem.</p>
          <p className="mt-2 font-mono text-xs uppercase tracking-widest2 text-ink-muted">
            Gere faixas pelas bandas; quando uma seção for prensada com áudio, ela aparece aqui.
          </p>
        </div>
      )}

      <ol className="flex flex-col gap-6">
        {visible.map((entry, i) => (
          <LibraryCard key={entry.song.project_id} entry={entry} index={i} />
        ))}
      </ol>
    </div>
  );
}

interface CardProps {
  entry: LibraryEntry;
  index: number;
}

function LibraryCard({ entry, index }: CardProps): JSX.Element {
  const { song, band, sections } = entry;
  const featureSlot = pickFeatureSlot(band);
  const reveal = `reveal reveal-${(index % 5) + 1}`;
  return (
    <li className={`${reveal} grid grid-cols-12 gap-4 border border-border bg-tapeElev p-4`}>
      <div className="col-span-12 flex items-start gap-3 md:col-span-3">
        <SongCover song={song} band={band} size={96} />
        <div className="flex flex-col gap-2">
          {featureSlot ? (
            <LpcAvatar
              layers={featureSlot.artist.lpc_layers}
              size={48}
              label={featureSlot.artist.name}
              action="idle"
              instrument={featureSlot.slot_role}
            />
          ) : (
            <div
              className="flex h-12 w-12 items-center justify-center border border-dashed border-border font-mono text-[9px] uppercase tracking-widest2 text-ink-muted"
              aria-label="sem artista"
            >
              ?
            </div>
          )}
          <div className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
            {band?.name ?? "sem banda"}
          </div>
        </div>
      </div>

      <div className="col-span-12 flex flex-col gap-2 md:col-span-9">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2
            className="font-display text-2xl leading-tight text-paper"
            style={{ fontVariationSettings: '"opsz" 48, "wght" 500, "SOFT" 60' }}
          >
            {song.title ?? <em className="text-ink-muted">sem título</em>}
          </h2>
          <span className="font-mono text-[10px] uppercase tracking-widest2 text-ink-dim">
            {new Date(song.created_at).toLocaleString("pt-BR")}
          </span>
        </div>
        <ul
          className="mt-1 flex flex-col gap-1"
          aria-label={`seções de ${song.title ?? song.project_id}`}
        >
          {sections.map((sec: LibrarySection) => (
            <li key={sec.id} className="flex items-center gap-3">
              <span className="w-24 shrink-0 font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
                {sec.name}
              </span>
              <audio
                controls
                preload="none"
                src={assetUrl(song.project_id, sec.assetHash)}
                className="flex-1"
                data-testid={`audio-${sec.id}`}
              />
            </li>
          ))}
        </ul>
        <div className="mt-2 flex justify-end">
          <Link to={`/songs/${song.project_id}`}>
            <Button variant="primary">abrir →</Button>
          </Link>
        </div>
      </div>
    </li>
  );
}
